import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatEur, formatDate, formatHours } from "@/lib/api";
import { toast } from "sonner";
import EditableField from "@/components/EditableField";
import EnseigneSelector from "@/components/EnseigneSelector";
import ActionButton from "@/components/ActionButton";
import BillingDocEditor, { openBillingPdf, PdfPreviewModal, ConvertMenu, PaymentModal, PAY_STATUTS, PAY_CLS, FAC_TYPE_LABEL } from "@/components/BillingDocEditor";
import { DemarcheEditModal, computeLimite } from "@/pages/Demarches";
import {
  ArrowLeft, FolderTree, Plus, Check, Clock, FileText, Lightbulb,
  StickyNote, X, ExternalLink, Phone, Mail, Eye, EyeOff, ChevronRight, CalendarClock, Pencil, Trash2, Upload, Loader2, Wallet,
  Handshake, Ruler, Receipt, Send, ShieldCheck, AlertTriangle
} from "lucide-react";

const CHRONO_ICONS = {
  handshake: Handshake, wallet: Wallet, ruler: Ruler, check: Check,
  receipt: Receipt, send: Send, "shield-check": ShieldCheck,
};

const CHRONO_HINTS = {
  contact_devis: "Voir / créer un devis",
  acompte: "Voir la finance",
  plans: "Ajouter un document",
  validation_client: "Basculer la validation client",
  facturation_solde: "Voir / créer une facture",
  demarches_envoyees: "Créer une démarche",
  demarches_validees: "Voir les démarches",
};

function ChronoTimeline({ steps, onToggle, onAction }) {
  if (!steps || steps.length === 0)
    return <div className="text-sm text-gray-400 italic">Chronologie indisponible.</div>;
  const n = steps.length;
  return (
    <div className="flex items-start w-full overflow-x-auto pb-1" data-testid="chrono-timeline">
      {steps.map((s, i) => {
        const Icon = CHRONO_ICONS[s.icon] || Check;
        const prevDone = i > 0 && steps[i - 1].done;
        return (
          <div key={s.key} data-testid={`chrono-step-${s.key}`} className="flex-1 min-w-[92px] flex flex-col items-center text-center relative px-0.5">
            {/* connectors */}
            {i > 0 && <span className={`absolute top-4 left-0 right-1/2 h-0.5 ${prevDone ? "bg-emerald-500" : "bg-gray-200"}`} />}
            {i < n - 1 && <span className={`absolute top-4 left-1/2 right-0 h-0.5 ${s.done ? "bg-emerald-500" : "bg-gray-200"}`} />}
            {/* node = valider / dévalider l'étape */}
            <button
              type="button"
              data-testid={`chrono-toggle-${s.key}`}
              onClick={() => onToggle(s.key)}
              title={s.done ? "Cliquer pour dévalider l'étape" : "Cliquer pour valider l'étape"}
              className={`relative z-10 shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition hover:scale-110 cursor-pointer
                ${s.done ? "bg-emerald-600 border-emerald-600 text-white"
                  : s.alert ? "bg-red-50 border-red-400 text-red-500 hover:border-red-600"
                  : "bg-white border-gray-300 text-gray-400 hover:border-gray-900 hover:text-gray-900"}`}
            >
              <Icon size={15} />
            </button>
            {/* label = ouvrir l'action liée */}
            <button
              type="button"
              data-testid={`chrono-action-${s.key}`}
              onClick={() => onAction(s)}
              title={CHRONO_HINTS[s.key]}
              className={`mt-2 text-[11px] leading-tight font-semibold break-words hover:underline cursor-pointer ${s.done ? "text-emerald-700" : s.alert ? "text-red-600" : "text-gray-900"}`}
            >
              {s.label}
            </button>
            <span className="mt-0.5 text-[10px] text-gray-400 leading-tight break-words">
              {s.done && s.date ? formatDate(s.date)
                : s.alert ? "En retard"
                : s.deadline ? `Éch. ${formatDate(s.deadline)}`
                : "En attente"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CustomStepsBlock({ projectId, steps, onChange }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [level, setLevel] = useState("");

  const add = async () => {
    if (!label.trim()) return;
    await api.post(`/projects/${projectId}/custom-steps`, { label: label.trim(), urgent_level: level || null });
    setLabel(""); setLevel(""); setAdding(false); onChange?.();
  };
  const toggle = async (sid) => { await api.post(`/projects/${projectId}/custom-steps/${sid}/toggle`); onChange?.(); };
  const remove = async (sid) => { await api.delete(`/projects/${projectId}/custom-steps/${sid}`); onChange?.(); };

  const LEVEL_PILL = {
    rouge: "bg-rose-100 text-rose-700 border-rose-200",
    orange: "bg-amber-100 text-amber-800 border-amber-200",
  };

  return (
    <div className="mt-5 pt-4 border-t border-stone-100" data-testid="custom-steps-block">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Étapes manuelles / tâches</div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="custom-step-add-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold text-stone-600 hover:text-stone-900 border border-stone-200 rounded-md px-2 py-1 hover:bg-stone-50">
            <Plus size={13} /> Ajouter une étape
          </button>
        )}
      </div>

      {adding && (
        <div className="flex flex-wrap items-center gap-2 mb-3 bg-stone-50 border border-stone-200 rounded-lg p-2.5" data-testid="custom-step-form">
          <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Ex: Relancer le fournisseur mobilier" data-testid="custom-step-label"
            className="flex-1 min-w-[180px] border border-stone-200 rounded-md px-2.5 py-1.5 text-sm" />
          <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="custom-step-level"
            className="border border-stone-200 rounded-md px-2 py-1.5 text-sm bg-white">
            <option value="">Sans urgence</option>
            <option value="rouge">Urgent · rouge (dashboard)</option>
            <option value="orange">À faire · orange (dashboard)</option>
          </select>
          <ActionButton onClick={add} data-testid="custom-step-save" successLabel="Ajouté"
            className="px-3 py-1.5 text-sm bg-stone-900 text-white rounded-md font-semibold">Ajouter</ActionButton>
          <button type="button" onClick={() => { setAdding(false); setLabel(""); setLevel(""); }}
            className="p-1.5 text-stone-400 hover:text-stone-700" data-testid="custom-step-cancel"><X size={16} /></button>
        </div>
      )}

      {steps.length === 0 && !adding && (
        <div className="text-xs text-stone-400 italic">Aucune étape manuelle. Ajoutez des tâches ponctuelles ou urgentes non prévues.</div>
      )}
      <div className="space-y-1.5">
        {steps.map((st) => (
          <div key={st.id} data-testid={`custom-step-${st.id}`}
            className="flex items-center gap-2 text-sm bg-white border border-stone-100 rounded-md px-2.5 py-1.5">
            <button type="button" onClick={() => toggle(st.id)} data-testid={`custom-step-toggle-${st.id}`}
              className={`w-5 h-5 rounded-full flex items-center justify-center border shrink-0 ${st.done ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-stone-300 text-transparent hover:border-stone-500"}`}>
              <Check size={12} />
            </button>
            <span className={`flex-1 ${st.done ? "line-through text-stone-400" : "text-stone-800"}`}>{st.label}</span>
            {st.urgent_level && !st.done && (
              <span className={`pill text-[10px] border ${LEVEL_PILL[st.urgent_level] || ""}`}>{st.urgent_level === "rouge" ? "urgent" : "à faire"}</span>
            )}
            <button type="button" onClick={() => remove(st.id)} data-testid={`custom-step-delete-${st.id}`}
              className="p-1 text-stone-300 hover:text-rose-600"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}


const CAT_LABELS = {
  production: "Production", reunion: "Réunion", deplacement: "Déplacement",
  commercial: "Commercial", admin_ppd: "Admin PPD", admin_fmob: "Admin F'Mob",
};
const STATUS_LABEL = { a_demarrer: "À démarrer", en_cours: "En cours", en_attente_client: "Attente client", en_attente_mairie: "Attente mairie", livre: "Livré", abandonne: "Abandonné" };
const DEM_STATUS = {
  a_creer: "À créer", en_preparation: "En préparation", depose: "Déposé",
  en_instruction: "En instruction", complement_demande: "Complément demandé",
  complement_envoye: "Complément envoyé", accorde: "Accordé", refuse: "Refusé", clos: "Clos",
};
const DEM_TYPES = ["AT_ERP", "AT_CENTRE_CO", "DP", "ENSEIGNE", "PC"];
const DEM_TYPE_LABEL = { AT_ERP: "AT-ERP", AT_CENTRE_CO: "AT-CENTRE CO", DP: "DP", ENSEIGNE: "ENSEIGNE", PC: "PC" };

function Section({ id, title, kicker, action, children }) {
  return (
    <section id={id} data-testid={`section-${id}`} className="space-y-3">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{kicker}</div>
          <h2 className="font-display text-xl font-semibold tracking-tight mt-0.5">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function QuickButton({ icon: Icon, label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:border-gray-900 hover:bg-gray-900 hover:text-white rounded-md text-xs font-semibold transition">
      <Icon size={14} /> {label}
    </button>
  );
}

function Gauge({ pct }) {
  const clamped = Math.min(pct, 130);
  const color = pct > 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div>
      <div className="flex justify-between text-xs font-semibold mb-1">
        <span className="text-gray-600">Consommation temps</span>
        <span className={pct > 100 ? "text-red-600" : pct >= 80 ? "text-amber-600" : "text-emerald-700"}>{pct.toFixed(0)} %</span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(clamped, 100)}%` }} />
      </div>
      {pct > 100 && <div className="text-[10px] text-red-600 mt-1 font-semibold">Dépassement budget temps</div>}
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl font-bold tracking-tight">{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddCustomFieldForm({ onAdd }) {
  const [k, setK] = useState("");
  const [v, setV] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!k.trim()) return;
    onAdd(k.trim(), v);
    setK(""); setV("");
  };
  const presets = ["Surface (m²)", "Budget travaux", "Bail signé", "Franchiseur", "Centre commercial", "Commercial référent"];
  return (
    <form onSubmit={submit} className="mt-3 flex flex-wrap gap-2 items-center" data-testid="cf-add-form">
      <input value={k} onChange={(e) => setK(e.target.value)} list="cf-presets" placeholder="Nom du champ" data-testid="cf-add-key" className="border border-stone-200 rounded px-2 py-1 text-xs w-44"/>
      <datalist id="cf-presets">{presets.map((p) => <option key={p} value={p}/>)}</datalist>
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder="Valeur" data-testid="cf-add-value" className="border border-stone-200 rounded px-2 py-1 text-xs flex-1 min-w-[120px]"/>
      <button type="submit" data-testid="cf-add-btn" className="text-xs px-3 py-1 bg-[#EB1F10] hover:bg-[#C71807] text-white rounded font-semibold">+ Ajouter</button>
    </form>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [full, setFull] = useState(null);
  const [assistMode, setAssistMode] = useState(false);
  const [modal, setModal] = useState(null); // 'time'|'note'|'idea'|'demarche'|'doc'|'link_p'|'link_f'
  const [editingDem, setEditingDem] = useState(null);
  const [demForm, setDemForm] = useState({ type: "AT_ERP", type_custom: "", reference: "", date_depot: "", zone_protegee: false, instructeur_nom: "", instructeur_email: "", instructeur_tel: "", plateforme: "", identifiant_mail: "", identifiant_mdp: "", commentaire: "" });
  const [allPrest, setAllPrest] = useState([]);
  const [allFour, setAllFour] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}/full`);
    setFull(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openLinkModal = async (kind) => {
    if (kind === "p") {
      const { data } = await api.get("/prestataires");
      setAllPrest(data);
    } else {
      const { data } = await api.get("/fournisseurs");
      setAllFour(data);
    }
    setModal(kind === "p" ? "link_p" : "link_f");
  };

  // Memoised filtered link lists — declared BEFORE any early return to respect Rules of Hooks.
  const linkablePrest = useMemo(
    () => allPrest.filter((pr) => !full?.project?.prestataires_ids?.includes(pr.id)),
    [allPrest, full?.project?.prestataires_ids],
  );
  const linkableFour = useMemo(
    () => allFour.filter((fo) => !full?.project?.fournisseurs_ids?.includes(fo.id)),
    [allFour, full?.project?.fournisseurs_ids],
  );

  if (!full) return <div className="text-sm text-gray-500" data-testid="project-loading">Chargement…</div>;

  const p = full.project;
  const k = full.kpis;
  const acompte_prevu = p.acompte_prevu || (p.montant_vendu * 0.33);

  // ===== Actions =====
  const submitTime = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    const hours = parseFloat(String(fd.hours || 0).replace(",", "."));
    if (!hours || hours <= 0) return;
    await api.post("/time", {
      category: fd.category,
      date: fd.date,
      notes: fd.notes,
      project_id: id,
      project_nom: p.nom,
      duration_min: Math.round(hours * 60),
    });
    setModal(null); load();
  };
  const submitNote = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    await api.post(`/projects/${id}/journal`, { ...fd, author: "" });
    setModal(null); load();
  };
  const submitIdea = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    await api.post("/ideas", { ...fd, project_id: id, project_nom: p.nom, brand: p.brand });
    setModal(null);
  };
  const submitDemarche = async (e) => {
    e.preventDefault();
    const f = demForm;
    const type = f.type === "__autre__" ? (f.type_custom || "").trim() : f.type;
    if (!type) return;
    await api.post("/demarches", {
      type, project_id: id, project_nom: p.nom,
      reference: f.reference || "", date_depot: f.date_depot || "", date_limite_auto: true,
      zone_protegee: !!f.zone_protegee,
      instructeur_nom: f.instructeur_nom || "", instructeur_email: f.instructeur_email || "", instructeur_tel: f.instructeur_tel || "",
      plateforme: f.plateforme || "", identifiant_mail: f.identifiant_mail || "", identifiant_mdp: f.identifiant_mdp || "",
      commentaire: f.commentaire || "",
    });
    setModal(null);
    setDemForm({ type: "AT_ERP", type_custom: "", reference: "", date_depot: "", zone_protegee: false, instructeur_nom: "", instructeur_email: "", instructeur_tel: "", plateforme: "", identifiant_mail: "", identifiant_mdp: "", commentaire: "" });
    load();
  };
  const submitDoc = async (e) => {
    e.preventDefault();
    if (!full.drive) await api.post(`/drive/from-project/${id}`);
    const drive = full.drive || (await api.get("/drive")).data.find((d) => d.project_id === id);
    const fd = Object.fromEntries(new FormData(e.target).entries());
    await api.post(`/drive/${drive.id}/file`, fd);
    setModal(null); load();
  };
  const toggleTimeline = async (evid) => {
    await api.post(`/projects/${id}/timeline/${evid}/toggle`);
    load();
  };
  const toggleChrono = async (key) => {
    await api.post(`/projects/${id}/chrono/${key}/toggle`);
    load();
  };
  const scrollToSection = (sid) => document.getElementById(sid)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const handleStepClick = (s) => {
    switch (s.key) {
      case "contact_devis":
      case "facturation_solde": scrollToSection("facturation"); break;
      case "acompte": scrollToSection("finance"); break;
      case "plans": setModal("doc"); break;
      case "validation_client": if (s.toggle_id) toggleTimeline(s.toggle_id); else scrollToSection("timeline"); break;
      case "demarches_envoyees": setModal("demarche"); break;
      case "demarches_validees": scrollToSection("demarches"); break;
      default: break;
    }
  };
  const linkItem = async (kind, itemId) => {
    await api.post(`/projects/${id}/link/${kind}/${itemId}`);
    setModal(null); load();
  };
  const unlinkItem = async (kind, itemId) => {
    await api.delete(`/projects/${id}/link/${kind}/${itemId}`);
    load();
  };
  const deleteDemarche = async (did) => {
    if (!window.confirm("Supprimer cette démarche ?")) return;
    try { await api.delete(`/demarches/${did}`); toast.success("Démarche supprimée"); load(); } catch (e) { /* toast via interceptor */ }
  };
  const createDrive = async () => {
    await api.post(`/drive/from-project/${id}`);
    load();
  };

  // Inline auto-save (used by EditableField)
  const saveField = async (field, value) => {
    await api.put(`/projects/${id}`, { [field]: value });
    load();
  };
  const saveCustomField = async (k, v) => {
    const cf = { ...(p.custom_fields || {}), [k]: v };
    await api.put(`/projects/${id}`, { custom_fields: cf });
    load();
  };
  const removeCustomField = async (k) => {
    const cf = { ...(p.custom_fields || {}) };
    delete cf[k];
    await api.put(`/projects/${id}`, { custom_fields: cf });
    load();
  };

  return (
    <div className="space-y-8" data-testid="project-detail-page">
      {/* Breadcrumb */}
      <Link to="/projets" className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1" data-testid="back-projects">
        <ArrowLeft size={14} /> Retour aux projets
      </Link>

      {/* Header sticky */}
      <div className="sticky top-0 z-30 -mx-4 md:-mx-8 px-4 md:px-8 bg-[#F7F7F9]/95 backdrop-blur-md border-b border-gray-200 py-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`pill ${p.brand === "PPD" ? "chip-ppd" : "chip-fmob"}`}>{p.brand === "PPD" ? "Pourquoi Pas Design" : "Franchi'Mob"}</span>
              <span className="pill bg-gray-100 text-gray-700">{STATUS_LABEL[p.status]}</span>
              {k.days_left !== null && (
                k.en_retard ? (
                  <span className="pill bg-red-50 text-red-700" data-testid="pd-retard-badge">
                    <CalendarClock size={11} className="mr-1 inline -mt-0.5" /> {Math.abs(k.days_left)}j retard
                  </span>
                ) : k.days_left < 0 ? (
                  <span className="pill bg-emerald-50 text-emerald-700" data-testid="pd-avenir-badge">
                    <CalendarClock size={11} className="mr-1 inline -mt-0.5" /> À jour · aucune action requise
                  </span>
                ) : (
                  <span className={`pill ${k.days_left < 14 ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
                    <CalendarClock size={11} className="mr-1 inline -mt-0.5" /> {k.days_left} j restants
                  </span>
                )
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">{p.nom}</h1>
            <div className="text-sm text-gray-600 mt-0.5">{p.client} · {p.ville || "—"} · {p.prestation || "—"}</div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <button
              data-testid="toggle-assist-mode"
              onClick={() => setAssistMode(!assistMode)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold transition border ${assistMode ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-200 hover:border-gray-900"}`}
            >
              {assistMode ? <EyeOff size={14} /> : <Eye size={14} />}
              {assistMode ? "Vue complète" : "Vue assistante"}
            </button>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="flex flex-wrap gap-2 mt-4">
          <QuickButton icon={Clock} label="Ajouter un temps" onClick={() => setModal("time")} testid="qa-time" />
          <QuickButton icon={StickyNote} label="Ajouter une note" onClick={() => setModal("note")} testid="qa-note" />
          <QuickButton icon={Lightbulb} label="Idée LinkedIn" onClick={() => setModal("idea")} testid="qa-idea" />
          <QuickButton icon={FileText} label="Nouvelle démarche" onClick={() => setModal("demarche")} testid="qa-demarche" />
          <QuickButton icon={FolderTree} label="Document Drive" onClick={() => setModal("doc")} testid="qa-doc" />
        </div>
      </div>

      {/* 1. Synthèse */}
      <Section id="synthese" kicker="01 · Synthèse projet" title="Identité & étapes">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card-flat p-5 lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <EditableField label="Nom du projet" value={p.nom} onSave={(v) => saveField("nom", v)} testid="ef-nom"/>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Enseigne</span>
                <EnseigneSelector
                  value={p.enseigne}
                  testid="ef-enseigne"
                  onSave={async (nom, franchiseur) => {
                    const patch = { enseigne: nom };
                    // Auto-remplir Franchiseur silencieusement si vide côté projet ET l'enseigne en propose un
                    if (franchiseur && !(p.franchiseur || "").trim()) {
                      patch.franchiseur = franchiseur;
                    }
                    await api.put(`/projects/${id}`, patch);
                    load();
                  }}
                />
              </div>
              <EditableField label="Franchiseur" value={p.franchiseur} onSave={(v) => saveField("franchiseur", v)} testid="ef-franchiseur"/>
              <EditableField label="Client" value={p.client} onSave={(v) => saveField("client", v)} testid="ef-client"/>
              <EditableField label="SIRET (client)" value={p.siret} onSave={(v) => saveField("siret", v)} testid="ef-siret"/>
              <EditableField label="Contact principal" value={p.contact_principal} onSave={(v) => saveField("contact_principal", v)} testid="ef-contact"/>
              <EditableField label="Téléphone" value={p.telephone} onSave={(v) => saveField("telephone", v)} testid="ef-tel"/>
              <EditableField label="Email" value={p.email} onSave={(v) => saveField("email", v)} testid="ef-email"/>
              <EditableField label="Adresse" value={p.adresse} onSave={(v) => saveField("adresse", v)} testid="ef-adresse"/>
              <EditableField label="Ville" value={p.ville} onSave={(v) => saveField("ville", v)} testid="ef-ville"/>
              <EditableField label="Démarrage" value={p.date_debut} onSave={(v) => saveField("date_debut", v)} type="date" testid="ef-debut"/>
              <EditableField label="Objectif" value={p.date_objectif} onSave={(v) => saveField("date_objectif", v)} type="date" testid="ef-objectif"/>
              <EditableField
                label="Statut"
                value={p.status}
                onSave={(v) => saveField("status", v)}
                type="select"
                testid="ef-status"
                options={[
                  { value: "a_demarrer", label: "À démarrer" },
                  { value: "en_cours", label: "En cours" },
                  { value: "en_attente_client", label: "Attente client" },
                  { value: "en_attente_mairie", label: "Attente mairie" },
                  { value: "livre", label: "Livré" },
                  { value: "abandonne", label: "Abandonné" },
                ]}
              />
              <EditableField
                label="Type de projet"
                value={p.type_projet}
                onSave={(v) => saveField("type_projet", v)}
                type="select"
                testid="ef-type"
                options={[
                  { value: "concept_franchise", label: "Concept franchise complet" },
                  { value: "projet_simple", label: "Projet simple / partiel" },
                  { value: "franchimob", label: "Franchi'Mob" },
                  { value: "administratif", label: "Administratif uniquement" },
                  { value: "mission_ponctuelle", label: "Mission ponctuelle" },
                ]}
              />
              <EditableField label="Prestation" value={p.prestation} onSave={(v) => saveField("prestation", v)} testid="ef-prestation"/>
              <EditableField label="Montant vendu (€)" value={p.montant_vendu} onSave={(v) => saveField("montant_vendu", v)} type="number" testid="ef-montant-vendu"/>
              <EditableField label="Temps prévu (h)" value={p.temps_prevu_h} onSave={(v) => saveField("temps_prevu_h", v)} type="number" testid="ef-temps-prevu"/>
            </div>

            {/* Démarches requises */}
            <label className="flex items-center gap-2 mt-4 text-sm font-semibold text-stone-700 bg-stone-50 border border-stone-100 rounded p-2.5 cursor-pointer w-fit">
              <input type="checkbox" checked={!!p.demarches_requises} onChange={(e) => saveField("demarches_requises", e.target.checked)} data-testid="ef-demarches-requises" className="accent-[var(--ppd)] w-4 h-4" />
              Démarches administratives à déposer
              {p.demarches_requises && <span className="pill bg-[var(--pink-powder)]/50 text-stone-700 text-[10px]">requis</span>}
            </label>

            {/* Champs personnalisés */}
            <div className="mt-5 pt-4 border-t border-stone-100">
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">Champs personnalisés</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {Object.entries(p.custom_fields || {}).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-2 group" data-testid={`cf-${k}`}>
                    <div className="flex-1">
                      <EditableField label={k} value={v} onSave={(nv) => saveCustomField(k, nv)} testid={`cf-${k}-field`}/>
                    </div>
                    <button onClick={() => removeCustomField(k)} title="Supprimer ce champ" className="text-stone-300 hover:text-red-600 opacity-0 group-hover:opacity-100 mt-4"><X size={12}/></button>
                  </div>
                ))}
              </div>
              <AddCustomFieldForm onAdd={(k, v) => saveCustomField(k, v)}/>
            </div>
          </div>

          <div className="card-flat p-5 bg-gradient-to-br from-stone-900 to-stone-800 text-white">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-3">Avancement</div>
            <div>
              <div className="text-xs text-stone-400 mb-1">Étape actuelle</div>
              <div className="font-display text-xl font-semibold">{k.etape_actuelle?.label || "Terminé"}</div>
              {k.etape_actuelle?.date && <div className="text-xs text-stone-400 mt-1">{formatDate(k.etape_actuelle.date)}</div>}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10">
              <div className="text-xs text-stone-400 mb-1">Étape suivante</div>
              <div className="text-sm font-medium">{k.etape_suivante?.label || "—"}</div>
            </div>
            <div className="mt-5 pt-4 border-t border-white/10">
              <div className="text-xs text-stone-400">Jours restants</div>
              <div className={`kpi-number text-3xl mt-1 ${k.days_left !== null && k.days_left < 0 ? "text-orange-400" : ""}`}>
                {k.days_left ?? "—"}<span className="text-base text-stone-400"> j</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Finance + 3. Rentabilité (cachés en mode assistante) */}
      {!assistMode && (
        <>
          <Section id="finance" kicker="02 · Finance" title="Vue économique (HT)">
            <FinanceSection p={p} k={k} id={id} onReload={load} saveField={saveField}/>
          </Section>

          <Section id="rentabilite" kicker="03 · Rentabilité" title="Temps & efficacité (HT)">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="card-flat p-5 lg:col-span-2">
                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div data-testid="rentab-temps-vendu">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Temps vendu</div>
                    <EditableField value={p.temps_prevu_h} onSave={(v) => saveField("temps_prevu_h", v)} type="number" testid="ef-temps-vendu" className="kpi-number text-2xl mt-1"/>
                  </div>
                  <div data-testid="rentab-temps-conso">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1">
                      Consommé
                      {p.temps_consomme_override != null && <span className="pill bg-amber-50 text-amber-800 text-[9px]">ajusté</span>}
                    </div>
                    <EditableField
                      value={p.temps_consomme_override != null ? p.temps_consomme_override : k.temps_consomme_h}
                      onSave={(v) => saveField("temps_consomme_override", v === 0 ? null : v)}
                      type="number"
                      testid="ef-temps-conso"
                      className="kpi-number text-2xl mt-1"
                    />
                    <div className="text-[10px] text-stone-400 mt-1">Auto : {formatHours(k.temps_consomme_auto_h ?? k.temps_consomme_h)}</div>
                  </div>
                  <div data-testid="rentab-temps-restant">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Restant</div>
                    <div className={`kpi-number text-2xl mt-1 font-mono ${(k.temps_restant_h ?? 0) < 0 ? "text-[var(--ppd)]" : ""}`} data-testid="kpi-temps-restant">
                      {formatHours(k.temps_restant_h ?? 0)}
                    </div>
                    <div className="text-[10px] text-stone-400 mt-1">= Vendu − Consommé</div>
                  </div>
                </div>
                <Gauge pct={k.temps_pct} />
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-stone-100">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Taux conso</div>
                    <div className="font-mono text-lg font-bold">{(k.taux_consommation ?? 0).toFixed(1)} %</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Taux horaire réel</div>
                    <div className="font-mono text-lg font-bold">{formatEur(k.taux_horaire_reel)}<span className="text-xs text-stone-500">/h</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Situation</div>
                    {(() => {
                      const cfg = {
                        rentable: { label: "Rentable", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
                        surveiller: { label: "À surveiller", cls: "bg-amber-50 text-amber-800 border-amber-200" },
                        depassement: { label: "Dépassement", cls: "bg-rose-50 text-rose-800 border-rose-200" },
                      }[k.situation] || { label: "—", cls: "bg-stone-100 text-stone-700" };
                      return <div className={`mt-1 inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`} data-testid="situation-pill">{cfg.label}</div>;
                    })()}
                  </div>
                </div>
              </div>
              <div className="card-flat p-5">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-3">Répartition</div>
                <div className="space-y-2 text-sm">
                  {Object.keys(CAT_LABELS).map((cat) => {
                    const m = k.cats_min[cat] || 0;
                    return (
                      <div key={cat} className="flex justify-between">
                        <span className="text-stone-600">{CAT_LABELS[cat]}</span>
                        <span className="font-mono font-semibold">{formatHours(m / 60)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-stone-400 mt-4 pt-3 border-t border-stone-100">
                  Tous les calculs sont en <span className="font-semibold">HT</span>. La TVA n'impacte jamais la marge ni le taux horaire.
                </div>
              </div>
            </div>
            <ProjectTimeEntries projectId={id} onReload={load} />
          </Section>
        </>
      )}

      {/* 4. Démarches */}
      <Section
        id="demarches"
        kicker="04 · Administratif"
        title="Démarches"
        action={<QuickButton icon={Plus} label="Démarche" onClick={() => setModal("demarche")} testid="add-demarche-btn" />}
      >
        {full.demarches.length === 0 ? (
          <div className="text-sm text-gray-400 italic">Aucune démarche pour ce projet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {full.demarches.map((d) => (
              <div key={d.id} onClick={() => setEditingDem(d)} className="card-flat p-4 cursor-pointer hover:border-[var(--ppd)]/40 hover:shadow-sm transition" data-testid={`pdem-${d.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="pill bg-gray-100 text-gray-700">{DEM_TYPE_LABEL[d.type] || d.type}</span>
                  <div className="flex items-center gap-2">
                    <span className="pill bg-amber-50 text-amber-700">{DEM_STATUS[d.status]}</span>
                    <button onClick={(e) => { e.stopPropagation(); setEditingDem(d); }} data-testid={`pdem-edit-${d.id}`} title="Modifier la démarche" className="text-gray-300 hover:text-[var(--ppd)]"><Pencil size={13}/></button>
                    <button onClick={(e) => { e.stopPropagation(); deleteDemarche(d.id); }} data-testid={`pdem-del-${d.id}`} title="Supprimer la démarche" className="text-gray-300 hover:text-rose-600"><X size={14}/></button>
                  </div>
                </div>
                <div className="font-semibold text-sm">{d.reference || "Sans référence"}</div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-gray-600">
                  <div><span className="text-gray-400">Dépôt:</span> {formatDate(d.date_depot)}</div>
                  <div><span className="text-gray-400">Limite:</span> {formatDate(d.date_limite)}</div>
                  {d.instructeur_nom && <div className="col-span-2 pt-2 border-t border-gray-100"><span className="text-gray-400">Instructeur:</span> {d.instructeur_nom}</div>}
                  {!assistMode && d.instructeur_tel && <div className="flex items-center gap-1"><Phone size={11}/> {d.instructeur_tel}</div>}
                  {!assistMode && d.instructeur_email && <div className="flex items-center gap-1 truncate"><Mail size={11}/> {d.instructeur_email}</div>}
                  {!assistMode && d.plateforme && <div className="col-span-2 truncate"><span className="text-gray-400">Plateforme:</span> {d.plateforme}</div>}
                  {!assistMode && (d.identifiant_mail || d.identifiant_mdp || d.identifiants) && (
                    <div className="col-span-2 font-mono text-[10px] bg-gray-50 p-1 rounded break-all">
                      {d.identifiant_mail && <div>👤 {d.identifiant_mail}</div>}
                      {d.identifiant_mdp && <div>🔑 {d.identifiant_mdp}</div>}
                      {!d.identifiant_mail && !d.identifiant_mdp && d.identifiants}
                    </div>
                  )}
                  {d.commentaire && <div className="col-span-2 text-gray-500 italic border-t border-gray-100 pt-2">{d.commentaire}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 4bis. Devis & Factures */}
      <ProjectBillingSection projectId={id} />

      {/* 5. Timeline chronologique */}
      <Section id="timeline" kicker="05 · Chronologie" title="Timeline projet">
        <div className="card-flat p-5">
          <ChronoTimeline steps={full.chronologie} onToggle={toggleChrono} onAction={handleStepClick} />
          <CustomStepsBlock
            projectId={id}
            steps={p.custom_steps || []}
            onChange={load}
          />
        </div>
      </Section>

      {/* 6. Journal */}
      <Section
        id="journal"
        kicker="06 · Activité"
        title="Journal d'activité"
        action={<QuickButton icon={Plus} label="Note" onClick={() => setModal("note")} testid="add-note-btn" />}
      >
        <div className="card-flat p-5">
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {(p.journal || []).slice().reverse().map((j) => (
              <div key={j.id} className="border-l-2 border-gray-200 pl-3 py-1">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-gray-500">
                  <span>{j.type}</span><span className="text-gray-300">·</span>
                  <span>{formatDate(j.created_at)}</span>
                </div>
                <div className="text-sm mt-0.5">{j.content}</div>
                <div className="text-xs text-gray-400 mt-0.5">— {j.author || "—"}</div>
              </div>
            ))}
            {(!p.journal || p.journal.length === 0) && <div className="text-sm text-gray-400 italic">Aucune note. Démarrez votre journal en cliquant sur "Ajouter une note".</div>}
          </div>
        </div>
      </Section>

      {/* 7. Documents */}
      <Section
        id="documents"
        kicker="07 · Documents"
        title="Espace Drive & Admin"
      >
        <ProjectDocuments projectId={id} lienDrive={p.lien_drive} saveField={saveField} drive={full.drive} onReload={load}/>
      </Section>

      {/* 8. Prestataires */}
      <Section
        id="prestataires"
        kicker="08 · Équipe"
        title="Prestataires liés"
        action={<QuickButton icon={Plus} label="Lier prestataire" onClick={() => openLinkModal("p")} testid="link-prest-btn" />}
      >
        {full.prestataires.length === 0 ? (
          <div className="text-sm text-gray-400 italic">Aucun prestataire lié.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {full.prestataires.map((pr) => (
              <div key={pr.id} className="card-flat p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-display font-semibold">{pr.nom}</div>
                    <span className="pill bg-gray-100 text-gray-600 mt-1 inline-block">{pr.metier}</span>
                  </div>
                  <button data-testid={`unlink-p-${pr.id}`} onClick={() => unlinkItem("prestataire", pr.id)} className="text-gray-300 hover:text-red-600"><X size={14}/></button>
                </div>
                <div className="mt-3 text-xs text-gray-600 space-y-0.5">
                  {pr.contact && <div>{pr.contact}</div>}
                  {pr.telephone && <div className="flex items-center gap-1"><Phone size={11}/> {pr.telephone}</div>}
                  {pr.email && <a href={`mailto:${pr.email}`} className="flex items-center gap-1 hover:text-gray-900"><Mail size={11}/> {pr.email}</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 9. Fournisseurs */}
      <Section
        id="fournisseurs"
        kicker="09 · Sourcing"
        title="Fournisseurs liés"
        action={<QuickButton icon={Plus} label="Lier fournisseur" onClick={() => openLinkModal("f")} testid="link-four-btn" />}
      >
        {full.fournisseurs.length === 0 ? (
          <div className="text-sm text-gray-400 italic">Aucun fournisseur lié.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {full.fournisseurs.map((fo) => (
              <div key={fo.id} className="card-flat p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-display font-semibold">{fo.nom}</div>
                    <div className="flex gap-1 flex-wrap mt-1">{fo.tags?.slice(0, 3).map((t) => <span key={t} className="pill bg-gray-100 text-gray-600">{t}</span>)}</div>
                  </div>
                  <button data-testid={`unlink-f-${fo.id}`} onClick={() => unlinkItem("fournisseur", fo.id)} className="text-gray-300 hover:text-red-600"><X size={14}/></button>
                </div>
                <div className="mt-3 text-xs text-gray-600 space-y-0.5">
                  {fo.contact && <div>{fo.contact}</div>}
                  {fo.email && <div className="truncate">{fo.email}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 10. Temps saisis (hide in assist mode) */}
      {!assistMode && (
        <Section
          id="temps"
          kicker="10 · Historique"
          title="Temps saisis"
          action={<QuickButton icon={Plus} label="Saisir un temps" onClick={() => setModal("time")} testid="add-time-btn" />}
        >
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500 border-b">
                <tr><th className="px-4 py-2.5 text-left">Date</th><th className="px-4 py-2.5 text-left">Utilisateur</th><th className="px-4 py-2.5 text-left">Catégorie</th><th className="px-4 py-2.5 text-right">Durée</th><th className="px-4 py-2.5 text-left">Note</th></tr>
              </thead>
              <tbody>
                {full.time_entries.map((e) => (
                  <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{formatDate(e.date)}</td>
                    <td className="px-4 py-2">{e.user_name}</td>
                    <td className="px-4 py-2"><span className="pill bg-gray-100 text-gray-700">{CAT_LABELS[e.category] || e.category}</span></td>
                    <td className="px-4 py-2 text-right font-mono">{formatHours(e.duration_min / 60)}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{e.notes}</td>
                  </tr>
                ))}
                {full.time_entries.length === 0 && <tr><td colSpan={5} className="text-center text-gray-400 py-8">Aucune saisie. Démarrez en cliquant sur "Saisir un temps".</td></tr>}
              </tbody>
              {full.time_entries.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-semibold"><td colSpan={3} className="px-4 py-2 text-right text-xs uppercase tracking-widest text-gray-500">Total consommé</td><td className="px-4 py-2 text-right font-mono">{formatHours(k.temps_consomme_h)}</td><td></td></tr>
                </tfoot>
              )}
            </table>
          </div>
        </Section>
      )}

      {/* ====== MODALS ====== */}
      {modal === "time" && (
        <Modal title="Saisir un temps" testid="modal-time" onClose={() => setModal(null)}>
          <form onSubmit={submitTime} className="space-y-3 text-sm">
            <select name="category" required className="w-full border border-gray-300 rounded px-3 py-2">{Object.entries(CAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Temps passé (heures)</span>
                <input name="hours" type="text" inputMode="decimal" required defaultValue="1" placeholder="1,5" className="border border-gray-300 rounded px-3 py-2 font-mono" data-testid="modal-time-hours"/>
              </label>
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Date</span>
                <input name="date" type="date" defaultValue={new Date().toISOString().slice(0, 10)} className="border border-gray-300 rounded px-3 py-2"/>
              </label>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[0.5, 1, 2, 4, 7].map((h) => (
                <button
                  key={h}
                  type="button"
                  data-testid={`modal-time-quick-${String(h).replace(".", "_")}`}
                  onClick={(e) => { e.target.closest("form").querySelector('input[name="hours"]').value = String(h).replace(".", ","); }}
                  className="flex-1 min-w-[55px] text-xs border border-gray-300 rounded py-1.5 hover:bg-gray-900 hover:text-white font-semibold transition"
                >
                  {h === 7 ? "7 h" : formatHours(h)}
                </button>
              ))}
            </div>
            <input name="notes" placeholder="Note (optionnel)" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <button data-testid="submit-time" type="submit" className="w-full bg-gray-900 text-white py-2.5 rounded font-semibold">Enregistrer</button>
          </form>
        </Modal>
      )}

      {modal === "note" && (
        <Modal title="Ajouter une note" testid="modal-note" onClose={() => setModal(null)}>
          <form onSubmit={submitNote} className="space-y-3 text-sm">
            <select name="type" className="w-full border border-gray-300 rounded px-3 py-2"><option value="commentaire">Commentaire</option><option value="note">Note</option><option value="appel">Appel</option><option value="reunion">Réunion</option><option value="email">Email</option></select>
            <textarea name="content" required rows={4} placeholder="Contenu…" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <button data-testid="submit-note" type="submit" className="w-full bg-gray-900 text-white py-2.5 rounded font-semibold">Enregistrer</button>
          </form>
        </Modal>
      )}

      {modal === "idea" && (
        <Modal title="Capturer une idée LinkedIn" testid="modal-idea" onClose={() => setModal(null)}>
          <form onSubmit={submitIdea} className="space-y-3 text-sm">
            <input name="title" required placeholder="Titre / accroche" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <textarea name="capture" rows={4} placeholder="Capture rapide…" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <input name="theme" placeholder="Thème (retour_experience, expertise…)" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <button data-testid="submit-idea" type="submit" className="w-full bg-[#D05E44] text-white py-2.5 rounded font-semibold">Capturer</button>
          </form>
        </Modal>
      )}

      {modal === "demarche" && (() => {
        const f = demForm;
        const setF = (k, v) => setDemForm((p) => ({ ...p, [k]: v }));
        const effType = f.type === "__autre__" ? "" : f.type;
        const limite = computeLimite(effType, f.date_depot, f.zone_protegee, null);
        const abfApplies = ["DP", "ENSEIGNE", "PC"].includes(effType);
        return (
        <Modal title="Nouvelle démarche" testid="modal-demarche" onClose={() => setModal(null)}>
          <form onSubmit={submitDemarche} className="space-y-3 text-sm">
            <select value={f.type} onChange={(e) => setF("type", e.target.value)} data-testid="pdc-type" className="w-full border border-gray-300 rounded px-3 py-2">
              {DEM_TYPES.map((t) => <option key={t} value={t}>{DEM_TYPE_LABEL[t] || t}</option>)}
              <option value="__autre__">Autre démarche…</option>
            </select>
            {f.type === "__autre__" && (
              <input value={f.type_custom} onChange={(e) => setF("type_custom", e.target.value)} placeholder="Intitulé de la démarche" data-testid="pdc-type-custom" className="w-full border border-gray-300 rounded px-3 py-2"/>
            )}
            {abfApplies && (
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-100 rounded p-2.5">
                <input type="checkbox" checked={f.zone_protegee} onChange={(e) => setF("zone_protegee", e.target.checked)} data-testid="pdc-abf" className="accent-[var(--ppd)]"/>
                ABF (site protégé) {effType === "PC" ? "→ +1 mois" : "→ 2 mois"}
              </label>
            )}
            <input value={f.reference} onChange={(e) => setF("reference", e.target.value)} placeholder="Référence dossier" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Date de dépôt</span>
                <input type="date" value={f.date_depot} onChange={(e) => setF("date_depot", e.target.value)} data-testid="pdc-depot" className="border border-gray-300 rounded px-3 py-2"/></label>
              <label className="flex flex-col"><span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Échéance (auto)</span>
                <input type="date" value={limite} readOnly data-testid="pdc-limite" className="border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-500"/></label>
            </div>
            <input value={f.instructeur_nom} onChange={(e) => setF("instructeur_nom", e.target.value)} placeholder="Instructeur" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <div className="grid grid-cols-2 gap-2">
              <input value={f.instructeur_email} onChange={(e) => setF("instructeur_email", e.target.value)} placeholder="Email instructeur" className="border border-gray-300 rounded px-3 py-2"/>
              <input value={f.instructeur_tel} onChange={(e) => setF("instructeur_tel", e.target.value)} placeholder="Téléphone" className="border border-gray-300 rounded px-3 py-2"/>
            </div>
            <input value={f.plateforme} onChange={(e) => setF("plateforme", e.target.value)} placeholder="Plateforme numérique (URL)" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <div className="border border-gray-100 bg-gray-50 rounded p-2.5 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Identifiants</div>
              <input value={f.identifiant_mail} onChange={(e) => setF("identifiant_mail", e.target.value)} placeholder="Adresse mail" data-testid="pdc-id-mail" className="w-full border border-gray-300 rounded px-3 py-2"/>
              <input value={f.identifiant_mdp} onChange={(e) => setF("identifiant_mdp", e.target.value)} placeholder="Mot de passe" data-testid="pdc-id-mdp" className="w-full border border-gray-300 rounded px-3 py-2"/>
            </div>
            <textarea value={f.commentaire} onChange={(e) => setF("commentaire", e.target.value)} placeholder="Commentaires (suivi)…" rows={2} data-testid="pdc-commentaire" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <button data-testid="submit-demarche" type="submit" className="w-full bg-gray-900 text-white py-2.5 rounded font-semibold">Créer</button>
          </form>
        </Modal>
        );
      })()}

      {editingDem && (
        <DemarcheEditModal
          demarche={editingDem}
          projects={[{ id, nom: p.nom }]}
          onClose={() => setEditingDem(null)}
          onSaved={() => { setEditingDem(null); load(); }}
        />
      )}

      {modal === "doc" && (
        <Modal title="Nouveau document" testid="modal-doc" onClose={() => setModal(null)}>
          <form onSubmit={submitDoc} className="space-y-3 text-sm">
            <select name="subfolder" className="w-full border border-gray-300 rounded px-3 py-2">
              {["01_ADMINISTRATIF", "02_PLANS", "03_VISUELS", "04_CLIENT", "05_FACTURATION", "06_LIVRABLES"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <input name="type" required placeholder="TYPE (DEVIS, PLAN, FACTURE…)" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <input name="name" required placeholder="Nom du document" className="w-full border border-gray-300 rounded px-3 py-2"/>
            <button data-testid="submit-doc" type="submit" className="w-full bg-gray-900 text-white py-2.5 rounded font-semibold">Ajouter</button>
          </form>
        </Modal>
      )}

      {modal === "link_p" && (
        <Modal title="Lier un prestataire" testid="modal-link-p" onClose={() => setModal(null)}>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {linkablePrest.map((pr) => (
              <button key={pr.id} data-testid={`pick-p-${pr.id}`} onClick={() => linkItem("prestataire", pr.id)} className="w-full text-left card-flat p-3 hover:border-gray-900">
                <div className="font-semibold">{pr.nom}</div>
                <span className="pill bg-gray-100 text-gray-600 mt-1 inline-block">{pr.metier}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {modal === "link_f" && (
        <Modal title="Lier un fournisseur" testid="modal-link-f" onClose={() => setModal(null)}>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {linkableFour.map((fo) => (
              <button key={fo.id} data-testid={`pick-f-${fo.id}`} onClick={() => linkItem("fournisseur", fo.id)} className="w-full text-left card-flat p-3 hover:border-gray-900">
                <div className="font-semibold">{fo.nom}</div>
                <div className="flex gap-1 flex-wrap mt-1">{fo.tags?.map((t) => <span key={t} className="pill bg-gray-100 text-gray-600">{t}</span>)}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}


function RetroCommissionCard({ project, onReload }) {
  const status = project.retrocommission_status || "a_venir";
  const taux = project.retrocommission_taux || 0;
  const montant = project.retrocommission || 0;
  const franchiseur = project.franchiseur || "";
  const manuelle = !!project.retrocommission_manuelle;
  const exclu = !!project.retrocommission_exclu;

  const setManualAmount = async (v) => {
    const n = Number(v) || 0;
    await api.put(`/projects/${project.id}`, { retrocommission: n, retrocommission_manuelle: true });
    onReload();
  };
  const resetAuto = async () => {
    if (!confirm("Réactiver le calcul automatique de la rétrocommission depuis le taux du franchiseur ?")) return;
    await api.put(`/projects/${project.id}`, { retrocommission_manuelle: false });
    await api.post(`/projects/${project.id}/retrocommission/recalc`);
    onReload();
  };
  const toggleExclu = async () => {
    await api.post(`/projects/${project.id}/retrocommission/toggle-exclu`, { exclu: !exclu });
    onReload();
  };
  const markPaid = async () => {
    if (!confirm("Marquer cette rétrocommission comme payée ?")) return;
    await api.post(`/projects/${project.id}/retrocommission/pay`);
    onReload();
  };
  const unmark = async () => {
    await api.post(`/projects/${project.id}/retrocommission/unpay`);
    onReload();
  };

  return (
    <div className={`card-flat p-5 md:col-span-1 ${exclu ? "bg-stone-50 border-stone-200" : "bg-rose-50/30 border-rose-200/60"}`} data-testid="retro-card">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1">
          <div className={`text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1 ${exclu ? "text-stone-500" : "text-rose-700"}`}>
            Rétrocommission HT
            {manuelle && !exclu && <span className="pill bg-rose-100 text-rose-800 text-[9px]">manuel</span>}
            <Pencil size={9} className={exclu ? "text-stone-300" : "text-rose-400"}/>
          </div>
          <EditableField
            value={montant}
            onSave={setManualAmount}
            type="number"
            testid="retro-amount-edit"
            className={`kpi-number text-2xl mt-1 ${exclu ? "text-stone-400 line-through" : "text-rose-800"}`}
          />
        </div>
        {/* Case à cocher pour EXCLURE du calcul (remplace le pill Due/À venir) */}
        <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] font-semibold uppercase tracking-wider text-stone-600 hover:text-stone-900 whitespace-nowrap" title="Si décoché, ce projet est retiré du calcul de rétrocommission">
          <input
            type="checkbox"
            checked={!exclu}
            onChange={toggleExclu}
            className="h-4 w-4 rounded border-stone-300 text-[var(--ppd)] focus:ring-[var(--ppd)]"
            data-testid="retro-exclu-toggle"
          />
          Inclus
        </label>
      </div>
      <div className="mt-1 text-[11px] text-stone-600 space-y-0.5">
        {exclu ? (
          <div className="italic text-stone-500">Projet retiré du calcul de rétrocommission. Coche « Inclus » pour le réintégrer.</div>
        ) : franchiseur ? (
          <>
            <div><span className="text-stone-400">Franchiseur :</span> <span className="font-semibold">{franchiseur}</span></div>
            <div><span className="text-stone-400">Taux :</span> <span className="font-mono font-semibold">{taux || 0}%</span> · {manuelle ? "édité manuellement" : (project.retrocommission_base_type === "facture_v2" ? "auto sur HT facturé" : "auto sur montant vendu (prévisionnel)")}</div>
            {!manuelle && project.retrocommission_base != null && (
              <div><span className="text-stone-400">Base de calcul :</span> <span className="font-mono font-semibold">{formatEur(project.retrocommission_base)}</span> HT{project.retrocommission_base_type === "facture_v2" ? " facturé" : " vendu"}</div>
            )}
            <div><span className="text-stone-400">Statut :</span> <span className="font-semibold">{status === "payee" ? "✓ Payée" : status === "due" ? "Due — à payer" : "À venir"}</span></div>
          </>
        ) : (
          <div className="italic text-stone-400">Aucun franchiseur lié — saisis le montant manuellement ou ajoute un franchiseur pour le calcul auto.</div>
        )}
      </div>
      {!exclu && (
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {status !== "payee" ? (
            <ActionButton onClick={markPaid} disabled={montant <= 0} data-testid="retro-mark-paid" successLabel="Payée" className="text-[11px] px-2.5 py-1 bg-stone-900 text-white rounded font-semibold disabled:opacity-40 disabled:cursor-not-allowed">Marquer payée</ActionButton>
          ) : (
            <button onClick={unmark} data-testid="retro-unmark" className="text-[11px] px-2.5 py-1 border border-stone-300 text-stone-700 rounded font-semibold">Annuler paiement</button>
          )}
          {manuelle && franchiseur && (
            <ActionButton onClick={resetAuto} data-testid="retro-reset-auto" successLabel="Recalculé" className="text-[11px] px-2.5 py-1 border border-rose-300 text-rose-700 rounded font-semibold hover:bg-rose-50">↺ Recalcul auto</ActionButton>
          )}
          {franchiseur && (
            <Link to={`/franchiseurs/${encodeURIComponent(franchiseur)}`} className="text-[11px] px-2.5 py-1 border border-stone-200 text-stone-600 rounded hover:bg-stone-50">Ajuster le taux →</Link>
          )}
        </div>
      )}
    </div>
  );
}


function FinanceSourceCard({ label, badge, value, onSave, testid, color }) {
  return (
    <div className="card-flat p-4 bg-stone-50/40">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
        {label}
        <Pencil size={9} className="text-stone-300"/>
        {badge && <span className="pill bg-stone-900 text-white text-[8px] uppercase">{badge}</span>}
      </div>
      <EditableField
        value={value}
        onSave={onSave}
        type="number"
        testid={testid}
        className={`kpi-number text-xl mt-1 ${color}`}
      />
    </div>
  );
}

function FinanceOverrideCard({ field, label, value, overridden, color = "text-gray-700", bg = "", projectId, onReload, size = "normal" }) {
  const save = async (v) => {
    await api.put(`/projects/${projectId}`, { [`${field}_override`]: Number(v) || 0 });
    onReload();
  };
  const resetAuto = async () => {
    await api.post(`/projects/${projectId}/finance/reset-override`, { fields: [field] });
    onReload();
  };
  const padding = size === "large" ? "p-5" : "p-4";
  const numberSize = size === "large" ? "text-2xl" : "text-xl";
  return (
    <div className={`card-flat ${padding} ${bg}`} data-testid={`finance-card-${field}`}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
        {label}
        <Pencil size={9} className="text-stone-300"/>
        {overridden && <span className="pill bg-amber-100 text-amber-800 text-[8px] uppercase" data-testid={`finance-override-pill-${field}`}>manuel</span>}
      </div>
      <EditableField
        value={value}
        onSave={save}
        type="number"
        testid={`ef-${field}`}
        className={`kpi-number ${numberSize} mt-1 ${color}`}
      />
      {overridden && (
        <button onClick={resetAuto} data-testid={`finance-reset-${field}`} className="mt-1 text-[10px] text-stone-500 hover:text-rose-700 flex items-center gap-0.5">
          ↺ Recalcul auto
        </button>
      )}
    </div>
  );
}


function FinanceSection({ p, k, id, onReload, saveField }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(null); // 'devis' | 'facture_st' | 'facture_avancement' | null

  const loadAttachments = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}/attachments`);
    setAttachments(data);
  }, [id]);
  useEffect(() => { loadAttachments(); }, [loadAttachments]);

  const acomptePct = Number(p.acompte_pct || 50);
  const montantVendu = Number(k?.montant_vendu || p.montant_vendu || 0);
  const acompteCalcule = Math.round(montantVendu * acomptePct / 100 * 100) / 100;

  const onUpload = async (file, doc_type, is_acompte = false) => {
    if (!file) return;
    setUploading(is_acompte ? "facture_acompte" : doc_type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", doc_type);
      fd.append("is_acompte", is_acompte ? "true" : "false");
      const { data } = await api.post(`/projects/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      const updated = data.updated_fields || [];
      if (updated.length > 0) {
        alert(`✅ PDF analysé : ${updated.map(u => `${u.field} = ${u.value} €`).join(", ")}`);
      } else if ((data.attachment?.montant_extrait || 0) > 0) {
        alert(`✅ PDF analysé : ${data.attachment.montant_extrait} € HT détectés. (Le montant vendu actuel a été conservé. Édite-le manuellement si besoin.)`);
      } else {
        alert("⚠️ PDF importé mais aucun montant détecté automatiquement.");
      }
      await loadAttachments();
      onReload();
    } catch (e) {
      alert("Échec de l'import : " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(null);
    }
  };

  const deleteAttachment = async (aid) => {
    if (!confirm("Supprimer ce PDF ?")) return;
    await api.delete(`/projects/${id}/attachments/${aid}`);
    await loadAttachments();
    onReload();
  };

  return (
    <div className="space-y-5">
      {/* 4 cartes principales — TOUT calculé depuis les PDF joints (lecture seule) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Montant vendu HT — somme des devis joints */}
        <div className="card-flat p-4 bg-stone-50/40">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
            Montant vendu HT
            <span className="pill bg-stone-900 text-white text-[8px] uppercase">référence</span>
          </div>
          <div className="kpi-number text-xl mt-1 text-stone-900 font-mono" data-testid="kpi-montant-vendu">{montantVendu.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</div>
          <div className="text-[10px] text-stone-400 mt-0.5">Σ devis joints</div>
          <UploadPdfButton testid="up-devis" label="+ Ajouter un devis" loading={uploading === "devis"} onFile={(f) => onUpload(f, "devis")}/>
        </div>

        {/* Acompte HT avec dropdown % */}
        <div className="card-flat p-4 bg-stone-50/40">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
            Acompte HT
            <select
              value={acomptePct}
              onChange={(e) => saveField("acompte_pct", Number(e.target.value))}
              className="text-[10px] font-bold bg-white border border-stone-300 rounded px-1 py-0.5"
              data-testid="ef-acompte-pct"
            >
              <option value="20">20%</option>
              <option value="30">30%</option>
              <option value="50">50%</option>
            </select>
          </div>
          <div className="kpi-number text-xl mt-1 text-stone-700 font-mono">{acompteCalcule.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</div>
          <div className="text-[10px] text-stone-400 mt-0.5">{acomptePct}% × montant vendu</div>
          <UploadPdfButton testid="up-facture-acompte" label="+ Facture d'acompte" loading={uploading === "facture_acompte"} onFile={(f) => onUpload(f, "facture_avancement", true)} size="sm"/>
        </div>

        {/* Reste à encaisser HT — vendu - encaissements réels */}
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Reste à encaisser HT</div>
          <div className="kpi-number text-xl mt-1 text-amber-700 font-mono" data-testid="kpi-reste-encaisser">{(k?.reste_encaisser || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</div>
          <div className="text-[10px] text-stone-400 mt-0.5">Vendu − encaissements réels</div>
          <UploadPdfButton testid="up-facture-av" label="+ Facture d'avancement" loading={uploading === "facture_avancement"} onFile={(f) => onUpload(f, "facture_avancement", false)} size="sm"/>
        </div>

        {/* Coût sous-traitance HT — somme des factures ST */}
        <div className="card-flat p-4 bg-stone-50/40">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Coût sous-traitance HT</div>
          <div className="kpi-number text-xl mt-1 text-stone-700 font-mono" data-testid="kpi-cout-st">{(k?.cout_sous_traitance || p.cout_sous_traitance || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</div>
          <div className="text-[10px] text-stone-400 mt-0.5">Σ factures sous-traitance</div>
          <UploadPdfButton testid="up-facture-st" label="+ Ajouter facture ST" loading={uploading === "facture_st"} onFile={(f) => onUpload(f, "facture_st")}/>
        </div>
      </div>

      {/* Bloc retro + marges */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RetroCommissionCard project={p} onReload={onReload}/>
        <div className="card-flat p-5 bg-emerald-50/40 border-emerald-200/60">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Marge prévisionnelle HT</div>
          <div className="kpi-number text-2xl mt-1 text-emerald-700 font-mono" data-testid="kpi-marge-prev">{formatEur(k.marge_prev)}</div>
          <div className="text-[10px] text-emerald-700/70 mt-1">= Vendu − Coût ST − Rétrocommission</div>
        </div>
        <MargeHoraireCard k={k}/>
      </div>

      {/* Documents joints — groupés par type avec retry IA + édit manuel du montant */}
      <div className="card-flat p-5">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-3 flex items-center gap-2">
          Documents joints ({attachments.length})
          {(() => {
            const unpaid = attachments.filter((a) => a.type === "facture_avancement" && a.paiement_statut === "impaye").length;
            return unpaid > 0 ? <span className="pill bg-rose-50 text-rose-700 text-[9px] normal-case" data-testid="finance-unpaid-badge">{unpaid} impayée{unpaid > 1 ? "s" : ""}</span> : null;
          })()}
        </div>
        {attachments.length === 0 ? (
          <div className="text-xs text-stone-400 italic">Aucun document joint. Importe un devis, une facture ou une facture ST pour alimenter les calculs automatiquement.</div>
        ) : (
          <div className="space-y-3">
            {[
              { key: "devis", label: "Devis", cls: "bg-stone-900 text-white", filter: (a) => a.type === "devis" },
              { key: "facture_acompte", label: "Facture d'acompte", cls: "bg-emerald-100 text-emerald-800", filter: (a) => a.type === "facture_avancement" && a.is_acompte },
              { key: "facture_avancement", label: "Factures d'avancement", cls: "bg-stone-100 text-stone-700", filter: (a) => a.type === "facture_avancement" && !a.is_acompte },
              { key: "facture_st", label: "Factures sous-traitance", cls: "bg-amber-100 text-amber-800", filter: (a) => a.type === "facture_st" },
            ].map((grp) => {
              const grpItems = attachments.filter(grp.filter);
              if (!grpItems.length) return null;
              const total = grpItems.reduce((s, a) => s + (Number(a.montant_extrait) || 0), 0);
              return (
                <div key={grp.key} className="border border-stone-100 rounded-lg p-2" data-testid={`attach-group-${grp.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`pill text-[10px] uppercase ${grp.cls}`}>{grp.label} · {grpItems.length}</span>
                    <span className="font-mono text-xs font-bold">{total.toLocaleString("fr-FR", {maximumFractionDigits: 0})} € HT</span>
                  </div>
                  {grpItems.map((a) => (
                    <AttachmentRow key={a.id} a={a} pid={id} onChange={async () => { await loadAttachments(); onReload(); }}/>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentRow({ a, pid, onChange }) {
  const [retrying, setRetrying] = useState(false);
  const isFacture = a.type === "facture_avancement" || a.type === "facture_st";
  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`/projects/${pid}/attachments/${a.id}/retry-extract`);
      await onChange();
    } catch (e) {
      alert("Échec analyse IA : " + (e.response?.data?.detail || e.message));
    } finally { setRetrying(false); }
  };
  const togglePaid = async () => {
    await api.put(`/projects/${pid}/attachments/${a.id}`, { paid: !a.paid });
    await onChange();
  };
  const editEcheance = async () => {
    const v = window.prompt("Date d'échéance de paiement (AAAA-MM-JJ).\nSi vide : date de facture + 7 jours.", a.echeance || "");
    if (v === null) return;
    await api.put(`/projects/${pid}/attachments/${a.id}`, { date_echeance: v.trim() });
    await onChange();
  };
  // Statut paiement : facture d'avancement = calcul échéance (backend) ; facture ST = suivi manuel simple
  const statut = a.type === "facture_avancement" ? a.paiement_statut : (a.paid ? "paye" : "impaye");
  const statutLabel = statut === "paye"
    ? <span className="text-emerald-700 font-semibold">Payée</span>
    : statut === "impaye"
      ? <span className="text-rose-700 font-semibold">Impayé</span>
      : <span className="text-stone-500 font-semibold">En attente</span>;
  const editMontant = async () => {
    const v = window.prompt("Saisir le montant HT correct (fallback manuel) :", a.montant_extrait || 0);
    if (v === null) return;
    const num = parseFloat(String(v).replace(",", "."));
    if (Number.isNaN(num)) return;
    await api.put(`/projects/${pid}/attachments/${a.id}`, { montant_extrait: num });
    await onChange();
  };
  const del = async () => {
    if (!confirm("Supprimer ce PDF ?")) return;
    await api.delete(`/projects/${pid}/attachments/${a.id}`);
    await onChange();
  };
  return (
    <div className="flex items-center gap-2 py-1.5 px-1 hover:bg-stone-50 rounded text-sm" data-testid={`attachment-${a.id}`}>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/attachments/${a.id}/file`} target="_blank" rel="noreferrer" className="flex-1 font-semibold hover:text-[var(--ppd)] truncate">📎 {a.filename}</a>
      {isFacture && (
        <div className="flex items-center gap-1.5 shrink-0">
          {a.type === "facture_avancement" && a.echeance && (
            <button onClick={editEcheance} className={`text-[10px] hover:text-[var(--ppd)] ${statut === "impaye" ? "text-rose-500 font-semibold" : "text-stone-400"}`} title="Modifier l'échéance de paiement" data-testid={`attachment-echeance-${a.id}`}>
              éch. {formatDate(a.echeance)}
            </button>
          )}
          <label className="flex items-center gap-1 text-[10px] cursor-pointer" title="Cocher si payée (prime sur l'échéance)">
            <input type="checkbox" checked={!!a.paid} onChange={togglePaid} data-testid={`attachment-paid-${a.id}`} className="accent-emerald-600 w-3.5 h-3.5"/>
            {statutLabel}
          </label>
        </div>
      )}
      <button onClick={editMontant} className="font-mono text-xs text-stone-700 hover:text-[var(--ppd)] border border-transparent hover:border-stone-300 px-1 rounded" title="Corriger le montant manuellement" data-testid={`attachment-edit-${a.id}`}>
        {a.montant_extrait ? `${Number(a.montant_extrait).toLocaleString("fr-FR", {maximumFractionDigits: 0})} €` : "0 €"} ✎
      </button>
      <button onClick={retry} disabled={retrying} title="Relancer l'analyse IA" className="text-stone-400 hover:text-emerald-700 text-xs disabled:opacity-40" data-testid={`attachment-retry-${a.id}`}>{retrying ? "..." : "↻"}</button>
      <span className="text-[10px] text-stone-400">{formatDate(a.uploaded_at)}</span>
      <button onClick={del} className="text-stone-400 hover:text-rose-600 text-xs" data-testid={`delete-attachment-${a.id}`}>✕</button>
    </div>
  );
}

function FinanceMainCard({ label, badge, value, onSave, testid, color, children }) {
  return (
    <div className="card-flat p-4 bg-stone-50/40">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
        {label}
        <Pencil size={9} className="text-stone-300"/>
        {badge && <span className="pill bg-stone-900 text-white text-[8px] uppercase">{badge}</span>}
      </div>
      <EditableField
        value={value}
        onSave={onSave}
        type="number"
        testid={testid}
        className={`kpi-number text-xl mt-1 ${color} font-mono`}
      />
      {children}
    </div>
  );
}

function UploadPdfButton({ label, loading, onFile, testid, size = "normal" }) {
  const inputRef = useRef(null);
  const padding = size === "sm" ? "px-2 py-1 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <>
      <input
        ref={inputRef} type="file" accept="application/pdf" hidden
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
        data-testid={`${testid}-input`}
      />
      <button
        type="button" disabled={loading} onClick={() => inputRef.current?.click()}
        className={`mt-2 ${padding} bg-stone-900 hover:bg-black text-white rounded font-semibold disabled:opacity-60 flex items-center gap-1`}
        data-testid={testid}
      >
        {loading ? "Analyse IA…" : `📎 ${label}`}
      </button>
    </>
  );
}

function MargeHoraireCard({ k }) {
  const mh = k?.marge_horaire_effective;
  const ben = k?.benefice_cumule;
  const obj = k?.tarif_objectif_horaire || 208;
  const cout = k?.cout_horaire_charge || 72;

  if (mh == null) {
    return (
      <div className="card-flat p-5 bg-stone-50 border-stone-200" data-testid="kpi-marge-horaire">
        <div className="text-[10px] uppercase tracking-widest text-stone-600 font-semibold">Marge horaire effective</div>
        <div className="kpi-number text-2xl mt-1 text-stone-400 font-mono">— €/h</div>
        <div className="text-[10px] text-stone-500 mt-1">En attente · objectif {obj} €/h · coût {cout} €/h</div>
      </div>
    );
  }

  let cls, accent, status;
  if (mh >= obj) {
    cls = "bg-emerald-50 border-emerald-300";
    accent = "text-emerald-700";
    status = `Au-dessus de l'objectif (+${Math.round(mh - obj)} €/h)`;
  } else if (mh >= cout) {
    cls = "bg-amber-50 border-amber-300";
    accent = "text-amber-800";
    status = `Sous l'objectif de ${Math.round(obj - mh)} €/h`;
  } else {
    cls = "bg-rose-50 border-rose-300";
    accent = "text-rose-700";
    status = `Sous le seuil de coût · alerte −${Math.round(cout - mh)} €/h`;
  }

  return (
    <div className={`card-flat p-5 border ${cls}`} data-testid="kpi-marge-horaire">
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${accent}`}>Marge horaire effective</div>
      <div className={`kpi-number text-2xl mt-1 font-mono ${accent}`} data-testid="kpi-marge-horaire-value">{Math.round(mh).toLocaleString("fr-FR")} €/h</div>
      <div className={`text-[10px] mt-1 ${accent}`}>Objectif {obj} €/h · coût {cout} €/h</div>
      <div className={`text-[11px] mt-1 font-semibold ${accent}`}>{status}</div>
      {ben != null && (
        <div className="text-[11px] mt-2 pt-2 border-t border-current/10">
          Bénéfice cumulé : <span className={`font-mono font-bold ${accent}`}>{Math.round(ben).toLocaleString("fr-FR")} €</span>
        </div>
      )}
    </div>
  );
}

function ProjectTimeEntries({ projectId, onReload }) {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ category: "production", hours: 1, date: new Date().toISOString().slice(0, 10), notes: "" });
  const loadEntries = useCallback(async () => {
    const { data } = await api.get(`/time?project_id=${projectId}`);
    setEntries(data);
  }, [projectId]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const add = async () => {
    const hours = parseFloat(String(form.hours).replace(",", "."));
    if (!hours || hours <= 0) return;
    await api.post("/time", { category: form.category, project_id: projectId, duration_min: Math.round(hours * 60), date: form.date, notes: form.notes });
    setForm({ ...form, hours: 1, notes: "" });
    await loadEntries();
    onReload && onReload();
  };
  const del = async (tid) => {
    await api.delete(`/time/${tid}`);
    await loadEntries();
    onReload && onReload();
  };

  return (
    <div className="card-flat p-5 mt-4" data-testid="project-time-entries">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-3 flex items-center gap-1.5"><Clock size={12}/> Saisies de temps du projet (liées à la Rentabilité)</div>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pt-category" className="border border-stone-300 rounded px-2 py-1.5 text-sm">
          {Object.entries(CAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="1,5" inputMode="decimal" data-testid="pt-hours" className="border border-stone-300 rounded px-2 py-1.5 text-sm font-mono w-20"/>
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border border-stone-300 rounded px-2 py-1.5 text-sm"/>
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note" className="border border-stone-300 rounded px-2 py-1.5 text-sm flex-1 min-w-[120px]"/>
        <ActionButton onClick={add} data-testid="pt-add" successLabel="Ajouté" className="bg-gray-900 text-white px-3 py-1.5 rounded text-sm font-semibold"><Plus size={13} className="inline -mt-0.5"/> Ajouter</ActionButton>
      </div>
      <div className="space-y-1 max-h-52 overflow-y-auto">
        {entries.length === 0 && <div className="text-xs text-stone-400 italic">Aucune saisie pour ce projet.</div>}
        {entries.map((e) => (
          <div key={e.id} className="flex items-center gap-3 py-1 border-b border-stone-100 text-xs group" data-testid={`pt-entry-${e.id}`}>
            <span className="font-mono text-stone-500 w-20">{formatDate(e.date)}</span>
            <span className="pill bg-stone-100 text-stone-700 capitalize text-[10px]">{CAT_LABELS[e.category] || e.category}</span>
            <span className="flex-1 truncate text-stone-600">{e.notes}</span>
            <span className="font-mono font-semibold">{formatHours(e.duration_min / 60)}</span>
            <button onClick={() => del(e.id)} data-testid={`pt-del-${e.id}`} className="text-stone-400 hover:text-rose-600 opacity-60 group-hover:opacity-100"><Trash2 size={13}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectDocuments({ projectId, lienDrive, saveField, drive }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const loadDocs = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/attachments`);
    setAttachments(data.filter((a) => a.type === "document"));
  }, [projectId]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "document");
      await api.post(`/projects/${projectId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadDocs();
    } catch (e) {
      alert("Échec de l'import : " + (e.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };
  const del = async (aid) => {
    if (!confirm("Supprimer ce document ?")) return;
    await api.delete(`/projects/${projectId}/attachments/${aid}`);
    await loadDocs();
  };

  return (
    <div className="card-flat p-5 space-y-4" data-testid="project-documents">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1 flex items-center gap-1.5"><ExternalLink size={11}/> Lien Drive</div>
        <div className="flex items-center gap-2">
          <div className="flex-1"><EditableField value={lienDrive} onSave={(v) => saveField("lien_drive", v)} placeholder="Coller un lien Drive (https://…)" testid="ef-lien-drive"/></div>
          {lienDrive && <a href={lienDrive} target="_blank" rel="noreferrer" className="text-xs text-[var(--ppd)] font-semibold inline-flex items-center gap-1 shrink-0"><ExternalLink size={12}/> Ouvrir</a>}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Fichiers joints ({attachments.length})</div>
          <label className="text-xs font-semibold text-[#C26B53] hover:text-[#A6452F] cursor-pointer flex items-center gap-1" data-testid="doc-upload-btn">
            {uploading ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>} {uploading ? "Envoi…" : "Ajouter un fichier"}
            <input type="file" disabled={uploading} className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }}/>
          </label>
        </div>
        {attachments.length === 0 ? (
          <div className="text-xs text-stone-400 italic">Aucun fichier joint. Déposez un PDF ou tout autre document lié au projet.</div>
        ) : (
          <div className="space-y-1">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 py-1.5 px-1 hover:bg-stone-50 rounded text-sm group" data-testid={`doc-${a.id}`}>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/attachments/${a.id}/file`} target="_blank" rel="noreferrer" className="flex-1 font-semibold hover:text-[var(--ppd)] truncate">📄 {a.filename}</a>
                <span className="text-[10px] text-stone-400">{formatDate(a.uploaded_at)}</span>
                <button onClick={() => del(a.id)} data-testid={`doc-del-${a.id}`} className="text-stone-400 hover:text-rose-600"><Trash2 size={13}/></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {drive && (
        <details className="text-xs">
          <summary className="cursor-pointer text-stone-500 font-semibold">Arborescence Drive type ({(drive.subfolders || []).length} dossiers)</summary>
          <div className="font-mono text-[11px] text-stone-500 mt-2">{drive.root_path}</div>
        </details>
      )}
    </div>
  );
}


const _BILL_STATUTS = { brouillon: "Brouillon", envoye: "Envoyé", signe: "Signé", refuse: "Refusé" };
const _BILL_CLS = { brouillon: "bg-stone-100 text-stone-600", envoye: "bg-blue-50 text-blue-700", signe: "bg-emerald-50 text-emerald-700", refuse: "bg-rose-50 text-rose-700" };

function ProjectBillingSection({ projectId }) {
  const [docs, setDocs] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [editor, setEditor] = useState(null);
  const [pay, setPay] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = useCallback(async () => {
    const [d, s] = await Promise.all([
      api.get(`/billing/docs?projet_id=${projectId}`),
      api.get(`/billing/societes`),
    ]);
    setDocs(d.data); setSocietes(s.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const validateFac = async (fid) => { await api.post(`/billing/docs/${fid}/validate`); load(); };
  const delFac = async (fid) => { if (!confirm("Supprimer ce document en brouillon ?")) return; try { await api.delete(`/billing/docs/${fid}`); load(); } catch (e) { /* toast interceptor */ } };

  return (
    <Section
      id="facturation"
      kicker="04bis · Commercial"
      title="Devis & Factures"
      action={<div className="flex gap-2">
        <QuickButton icon={Plus} label="Devis" onClick={() => setEditor({ docType: "devis" })} testid="proj-new-devis-btn" />
        <QuickButton icon={Plus} label="Facture" onClick={() => setEditor({ docType: "facture" })} testid="proj-new-facture-btn" />
      </div>}
    >
      {docs.length === 0 ? (
        <div className="text-sm text-gray-400 italic">Aucun document. Crée un devis : client, société et infos projet repris automatiquement.</div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const fac = (d.type || "").startsWith("facture");
            const draft = fac && d.statut === "brouillon";
            const reste = (Number(d.total_ttc) || 0) - (Number(d.montant_paye) || 0);
            return (
              <div key={d.id} className="card-flat p-3 flex items-center gap-3 flex-wrap" data-testid={`proj-doc-${d.id}`}>
                <span className="font-mono text-sm font-bold">{d.numero || (fac ? "Brouillon" : d.numero)}</span>
                {fac
                  ? <><span className="pill bg-stone-900 text-white text-[10px]">{FAC_TYPE_LABEL[d.type] || "Facture"}</span>
                     {draft ? <span className="pill bg-amber-50 text-amber-700 text-[10px]">Brouillon</span>
                            : <span className={`pill text-[10px] ${PAY_CLS[d.paiement_statut] || "bg-stone-100"}`}>{PAY_STATUTS[d.paiement_statut] || "—"}</span>}</>
                  : <span className={`pill text-[10px] ${_BILL_CLS[d.statut] || "bg-stone-100"}`}>{_BILL_STATUTS[d.statut] || d.statut}</span>}
                <button onClick={() => setPreview(d)} data-testid={`proj-doc-preview-${d.id}`} title="Aperçu du document" className="text-sm text-stone-700 flex-1 min-w-[120px] truncate text-left hover:text-[#C2502F] hover:underline cursor-pointer">{d.objet || d.client_nom}</button>
                <span className="font-mono text-sm font-semibold">{formatEur(d.total_ttc)} TTC</span>
                {fac && !draft && reste > 0.005 && <span className="text-xs text-amber-700 font-mono">reste {formatEur(reste)}</span>}
                {!fac && <ConvertMenu doc={d} onDone={load}/>}
                {fac && d.statut !== "annulee" && (
                  <label className="flex items-center gap-1 text-xs font-semibold text-stone-600 cursor-pointer" title={draft ? "Cocher pour valider (numéro définitif, facture immuable)" : "Facture validée (immuable)"}>
                    <input type="checkbox" checked={!draft} disabled={!draft} data-testid={`proj-fac-valider-chk-${d.id}`} onChange={() => { if (draft && confirm("Valider cette facture ? Numéro définitif, plus modifiable ni supprimable.")) validateFac(d.id); }} className="accent-emerald-600 w-4 h-4"/>
                    Validée
                  </label>
                )}
                {fac && <button onClick={() => setPay(d)} title={draft ? "Encaisser un paiement (valide la facture)" : "Paiements"} className="px-2.5 py-1.5 rounded text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"><Wallet size={13}/> Paiement</button>}
                {(!fac || draft) && <button onClick={() => setEditor({ doc: d })} className="p-1.5 text-stone-500 hover:text-stone-900" title="Éditer"><Pencil size={15}/></button>}
                {draft && <button onClick={() => delFac(d.id)} data-testid={`proj-doc-del-${d.id}`} className="p-1 text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-full" title="Supprimer (facture non validée)"><X size={16}/></button>}
                <button onClick={() => setPreview(d)} data-testid={`proj-doc-eye-${d.id}`} className="p-1.5 text-stone-500 hover:text-[#C2502F]" title="Aperçu"><Eye size={15}/></button>
                <button onClick={() => openBillingPdf(d.id, d.numero || "document")} className="p-1.5 text-stone-500 hover:text-stone-900" title="Télécharger le PDF"><FileText size={15}/></button>
              </div>
            );
          })}
        </div>
      )}
      {editor && (
        <BillingDocEditor
          doc={editor.doc || null}
          docType={editor.docType || "devis"}
          projectId={projectId}
          societes={societes}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
      {pay && <PaymentModal doc={pay} onClose={() => setPay(null)} onSaved={load}/>}
      {preview && <PdfPreviewModal doc={preview} onClose={() => setPreview(null)}/>}
    </Section>
  );
}
